import {Action, Dispatch} from "@reduxjs/toolkit"
import logger from "@next/core-logger"
import {GetFavouritesCallbackContractModel} from "@next/eventservice"
import type {AxiosError} from "axios"
import isEmpty from "lodash/isEmpty"
import {SummaryType, ColourwayData} from "../../models/productSummaryApi"
import {
    ProductType,
    FAV_ERROR_LIMIT_EXCEEDED,
    GTM_FAVOURITES,
    GTM_ADD_FAVOURITES,
    GTM_REMOVE_FAVOURITES,
    GTM_ERROR_ADD_FAVOURITES,
    GTM_ERROR_REMOVE_FAVOURITES,
    FAV_ADD,
    FAV_REMOVE,
    SHOW_INITIAL_COLOURCHIPS,
    SaleSashPosition,
    DEV_ESI_HEADER,
} from "../../config/constants"
import {getProductSummaryJson} from "../../api/getProductSummaryJson"
import {getColourways} from "../../api/getColourways"
import {
    ProductSummaryState,
    SummaryData,
    FavErrorToolTipType,
    MobileThumbnailConfig,
    ColourChipConfig,
    AkamaiImageParams,
    ImageCdnUrlParts,
} from "../../models/ProductSummary"
import State from "../../models/State"
import {shoppingListItemsToFavouritedColourways} from "../../utils/favourites"
import {PublishTrackEvent} from "../../events/gtm/trackEvent"
import {getProductTitle} from "../../utils/product"
import {AdditionalColourchipsLoadingState} from "../../models/AdditionalColourchipsLoadingState"
import {getTileSummaryData} from "../../utils/getTileSummaryData"
import {mapProductsFragmentJsonToItems, MapProductsFragmentJsonToItemsResponseItems} from "../../utils/productsFragment"
import {generateItemsString} from "../search/utils"
import {getProductSummaryAggregation} from "../../api/getProductSummaryAggregation"

type GetStateFn = () => State

export const SET_SUMMARY_ITEMS_DATA = "SET_SUMMARY_ITEMS_DATA"
export const SET_SELECTED_COLOURWAY = "SET_SELECTED_COLOURWAY"
export const SET_SELECTED_COLOURCHIP = "SET_SELECTED_COLOURCHIP"
export const SET_SELECTED_COLOURWAY_AND_COLOURCHIP = "SET_SELECTED_COLOURWAY_AND_COLOURCHIP"
export const ENABLE_FAV = "ENABLE_FAV"
export const SET_ENABLE_REVIEW_STARS = "SET_ENABLE_REVIEW_STARS"
export const SHOW_FAV_ERROR_TOOLTIP = "SHOW_FAV_ERROR_TOOLTIP"
export const SET_ENABLE_SEARCH_DESCRIPTION = "SET_ENABLE_SEARCH_DESCRIPTION"
export const SET_LOADING_FAV = "SET_LOADING_FAV"
export const SET_FAVOURITED_COLOURWAYS = "SET_FAVOURITED_COLOURWAYS"
export const SET_ANIMATE_FAVOURITE_ICON = "SET_ANIMATE_FAVOURITE_ICON"
export const SET_ENABLE_BRAND_DISPLAY = "SET_ENABLE_BRAND_DISPLAY"
export const SET_ENABLE_SINGLE_LINE_TITLE = "SET_ENABLE_SINGLE_LINE_TITLE"
export const SET_MOBILE_THUMBNAIL_CONFIG = "SET_MOBILE_THUMBNAIL_CONFIG"
export const SET_COLOUR_CHIP_CONFIG = "SET_COLOUR_CHIP_CONFIG"
export const SET_SALE_PRICE_TEXT_COLOR = "SET_SALE_PRICE_TEXT_COLOR"
export const SET_MAX_NUMBER_OF_SOFA_COLOURWAYS = "SET_MAX_NUMBER_OF_SOFA_COLOURWAYS"
export const SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS = "SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS"
export const SET_ADDITIONAL_COLOURHIPS_LOADING = "SET_ADDITIONAL_COLOURHIPS_LOADING"
export const SET_ADDITIONAL_COLOURWAYS = "SET_ADDITIONAL_COLOURWAYS"
export const SET_IMAGE_RATIO = "SET_IMAGE_RATIO"
export const SET_BRANDED_MARKEDOWN_SALE_PRICE = "SET_BRANDED_MARKEDOWN_SALE_PRICE"
export const SET_AKAMAI_IMAGE_PARAMS = "SET_AKAMAI_IMAGE_PARAMS"
export const SET_DISPLAY_FIT_ICONS = "SET_DISPLAY_FIT_ICONS"
export const SET_DISPLAY_NEW_IN = "SET_DISPLAY_NEW_IN"
export const SET_IMAGE_CDN_URL = "SET_IMAGE_CDN_URL"
export const SET_SALE_SASH_AND_POSITION = "SET_SALE_SASH_AND_POSITION"
export const UPDATE_SUMMARY_ITEMS_DATA = "UPDATE_SUMMARY_ITEMS_DATA"
export const POPULATE_PRODUCT_SUMMARY_STATE = "POPULATE_PRODUCT_SUMMARY_STATE"

export const initialState: ProductSummaryState = {
    summaryItemsData: {},
    enableReviewStars: false,
    enabledSearchDesc: false,
    enableBrandDisplay: false,
    enableSingleLineTitle: false,
    enableSingleLineBrandName: true,
    mobileThumbnailConfig: {
        xs: 6,
        sm: 8,
        md: 10,
    },
    colourChipConfig: {
        lg: 9,
        xl: 9,
    },
    maxNumberOfSofaColourways: null,
    maxNumberOfHomewareColourways: null,
    imageRatio: "2x3",
    brandedMarkdownSalePrice: false,
    akamaiImageParams: {
        lg: "",
        sm: "",
    },
    displayFitIcons: true,
    displayNewIn: true,
    saleSash: "OFFER_LEFT.png",
    saleSashPosition: SaleSashPosition.TL,
    imageCdnUrl: "https://xcdn.next.co.uk/Common/Items/Default/Default/ItemImages",
    productImageUrlPart: "Search",
    lgImagePath: "676",
    usePDPImage: false,
    savingsSashConfig: {
        sale: 0,
        clearance: 0,
    },
    enableFurtherReductionsSash: false,
    enableQuickAddToBag: false,
    bundlePriceLabel: "",
}

interface SetEnableSearchDescription {
    type: typeof SET_ENABLE_SEARCH_DESCRIPTION
    enabledSearchDesc: boolean
}

interface SetAkamaiImageParams {
    type: typeof SET_AKAMAI_IMAGE_PARAMS
    data: AkamaiImageParams
}

interface SetAdditionalColourchipsLoading {
    type: typeof SET_ADDITIONAL_COLOURHIPS_LOADING
    data: {itemNumber: string; additionalColourchipsLoading: AdditionalColourchipsLoadingState}
}

interface SetSummaryItemsData {
    type: typeof SET_SUMMARY_ITEMS_DATA
    data: {[key: string]: SummaryData}
}

interface UpdateSummaryItemsData {
    type: typeof UPDATE_SUMMARY_ITEMS_DATA
    data: {[key: string]: SummaryData}
}

interface SetAdditionalColourways {
    type: typeof SET_ADDITIONAL_COLOURWAYS
    data: {
        itemNumber: string
        colourwayData: ColourwayData[]
    }
}

interface SetLoadingFav {
    type: typeof SET_LOADING_FAV
    data: {itemNumber: string; isLoadingFav: boolean; animateFavouriteIcon: boolean}
}

interface SetAnimateFavouriteIcon {
    type: typeof SET_ANIMATE_FAVOURITE_ICON
    data: {itemNumber: string; animateFavouriteIcon: boolean}
}

interface SetSelectedColourway {
    type: typeof SET_SELECTED_COLOURWAY
    data: {itemNumber: string; colourwayItemNumber: string}
}

interface SetSelectedColourchip {
    type: typeof SET_SELECTED_COLOURCHIP
    data: {itemNumber: string; colourchipItemNumber: number}
}

interface SetSelectedColourwayAndColourchip {
    type: typeof SET_SELECTED_COLOURWAY_AND_COLOURCHIP
    data: {itemNumber: string; colourwayItemNumber: string; colourchipItemNumber: number; animateFavouriteIcon: boolean}
}

interface SetFavouritedColourways {
    type: typeof SET_FAVOURITED_COLOURWAYS
    data: {itemNumber: string; favouritedColourways: string[]}
}

interface SetEnableReviewStars {
    type: typeof SET_ENABLE_REVIEW_STARS
    enableReviewStars: boolean
}
interface SetEnableBrandDisplay {
    type: typeof SET_ENABLE_BRAND_DISPLAY
    enableBrandDisplay: boolean
}
interface SetEnableSingleLineTitle {
    type: typeof SET_ENABLE_SINGLE_LINE_TITLE
    enableSingleLineTitle: boolean
}
interface SetMobileThumbnailConfig {
    type: typeof SET_MOBILE_THUMBNAIL_CONFIG
    mobileThumbnailConfig: MobileThumbnailConfig
}

interface SetColourChipConfig {
    type: typeof SET_COLOUR_CHIP_CONFIG
    colourChipConfig: ColourChipConfig
}

interface SetMaxNumberOfSofaColourways {
    type: typeof SET_MAX_NUMBER_OF_SOFA_COLOURWAYS
    maxNumberOfSofaColourways: number | null
}

interface SetMaxNumberOfHomewareColourways {
    type: typeof SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS
    maxNumberOfHomewareColourways: number | null
}

interface SetImageRatio {
    type: typeof SET_IMAGE_RATIO
    imageRatio: string
}

interface SetBrandedMarkdownSalePrice {
    type: typeof SET_BRANDED_MARKEDOWN_SALE_PRICE
    brandedMarkdownSalePrice: boolean
}

interface SetDisplayFitIcons {
    type: typeof SET_DISPLAY_FIT_ICONS
    displayFitIcons: boolean
}

interface SetDisplayNewIn {
    type: typeof SET_DISPLAY_NEW_IN
    displayNewIn: boolean
}

interface SetImageCdnUrl {
    type: typeof SET_IMAGE_CDN_URL
    imageCdnUrl: ImageCdnUrlParts
}

interface SetSaleSashAndPosition {
    type: typeof SET_SALE_SASH_AND_POSITION
    data: {saleSash: string; saleSashPosition: SaleSashPosition}
}
interface SetShowFavErrorToolTip {
    type: typeof SHOW_FAV_ERROR_TOOLTIP
    data: {itemNumber: string; errorType: FavErrorToolTipType}
}

const setEnabledSearchDescription = (enabledSearchDesc: boolean) => ({
    type: SET_ENABLE_SEARCH_DESCRIPTION,
    enabledSearchDesc,
})

const setUpdateProductSummaryItemsData = (data: {[key: string]: SummaryData}): UpdateSummaryItemsData => {
    return {
        type: UPDATE_SUMMARY_ITEMS_DATA,
        data,
    }
}

const setProductSummaryItemsDataAction = (data: {[key: string]: SummaryData}): SetSummaryItemsData => {
    return {
        type: SET_SUMMARY_ITEMS_DATA,
        data,
    }
}

export const setAdditionalColourways = (data: {
    itemNumber: string
    colourwayData: ColourwayData[]
}): SetAdditionalColourways => {
    return {
        type: SET_ADDITIONAL_COLOURWAYS,
        data,
    }
}

export const setLoadingFav = (data: {
    itemNumber: string
    isLoadingFav: boolean
    animateFavouriteIcon: boolean
}): SetLoadingFav => {
    return {
        type: SET_LOADING_FAV,
        data,
    }
}

export const setAdditionalColourchipsLoading = (data: {
    itemNumber: string
    additionalColourchipsLoading: AdditionalColourchipsLoadingState
}): SetAdditionalColourchipsLoading => {
    return {
        type: SET_ADDITIONAL_COLOURHIPS_LOADING,
        data,
    }
}

export const setAnimateFavouriteIcon = (data: {
    itemNumber: string
    animateFavouriteIcon: boolean
}): SetAnimateFavouriteIcon => {
    return {
        type: SET_ANIMATE_FAVOURITE_ICON,
        data,
    }
}

export const setSelectedColourwayAction = (data: {
    itemNumber: string
    colourwayItemNumber: string
}): SetSelectedColourway => {
    return {
        type: SET_SELECTED_COLOURWAY,
        data,
    }
}

export const setSelectedColourchipAction = (data: {
    itemNumber: string
    colourchipItemNumber: number
}): SetSelectedColourchip => {
    return {
        type: SET_SELECTED_COLOURCHIP,
        data,
    }
}

export const setSelectedColourWayAndColourChipAction = (data: {
    itemNumber: string
    colourwayItemNumber: string
    colourchipItemNumber: number
    animateFavouriteIcon: boolean
}): SetSelectedColourwayAndColourchip => {
    return {
        type: SET_SELECTED_COLOURWAY_AND_COLOURCHIP,
        data,
    }
}

export const selectCanLoadVisibleColourways =
    (itemNumber: string) => (state: {productSummary: ProductSummaryState}) => {
        const selectedItem = getTileSummaryData(state.productSummary.summaryItemsData, itemNumber)
        return selectedItem.canLoadVisibleColourways
    }

export const setEnableReviewStars = (enableReviewStars: boolean) => ({
    type: SET_ENABLE_REVIEW_STARS,
    enableReviewStars,
})
export const setEnableBrandDisplay = (enableBrandDisplay: boolean) => ({
    type: SET_ENABLE_BRAND_DISPLAY,
    enableBrandDisplay,
})

export const setEnableSingleLineTitle = (enableSingleLineTitle: boolean) => ({
    type: SET_ENABLE_SINGLE_LINE_TITLE,
    enableSingleLineTitle,
})

export const setMobileThumbnailConfig = (mobileThumbnailConfig: MobileThumbnailConfig) => ({
    type: SET_MOBILE_THUMBNAIL_CONFIG,
    mobileThumbnailConfig,
})

export const setColourChipConfig = (colourChipConfig: ColourChipConfig) => ({
    type: SET_COLOUR_CHIP_CONFIG,
    colourChipConfig,
})

export const setFavouritedColourways = (data: {itemNumber: string; favouritedColourways: string[]}) => ({
    type: SET_FAVOURITED_COLOURWAYS,
    data,
})

export const setShowFavErrorToolTip = (data: {itemNumber: string; errorType: FavErrorToolTipType | null}) => ({
    type: SHOW_FAV_ERROR_TOOLTIP,
    data,
})

export const setMaxNumberOfSofaColourways = (maxNumberOfSofaColourways: number | null) => ({
    type: SET_MAX_NUMBER_OF_SOFA_COLOURWAYS,
    maxNumberOfSofaColourways,
})

export const setMaxNumberOfHomewareColourways = (maxNumberOfHomewareColourways: number | null) => ({
    type: SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS,
    maxNumberOfHomewareColourways,
})

export const SetImageRatio = (imageRatio: string) => ({
    type: SET_IMAGE_RATIO,
    imageRatio,
})

export const SetBrandedMarkdownSalePrice = (brandedMarkdownSalePrice: boolean) => ({
    type: SET_BRANDED_MARKEDOWN_SALE_PRICE,
    brandedMarkdownSalePrice,
})

export const SetDisplayFitIcons = (displayFitIcons: boolean) => ({
    type: SET_DISPLAY_FIT_ICONS,
    displayFitIcons,
})

export const SetDisplayNewIn = (displayNewIn: boolean) => ({
    type: SET_DISPLAY_NEW_IN,
    displayNewIn,
})

export const SetImageCdnUrl = (imageCdnUrl: ImageCdnUrlParts) => ({
    type: SET_IMAGE_CDN_URL,
    imageCdnUrl,
})

export const SetSaleSashAndPosition = (data: {saleSash: string; saleSashPosition: string}) => ({
    type: SET_SALE_SASH_AND_POSITION,
    data,
})

export const updateProductSummary = async ({dispatch, getState}: {dispatch: Dispatch; getState: GetStateFn}) => {
    try {
        const state = getState() as State
        const {
            search: {items, apiProxyVersion, templateOverride = {}, edgeAggregator, title},
            request: {headers, siteUrl, useDevEsi, plpHeaders},
            productSummary: {maxNumberOfSofaColourways, maxNumberOfHomewareColourways},
        } = state

        if (edgeAggregator) {
            const itemsString = generateItemsString(items, templateOverride)
            const fragment = await getProductSummaryAggregation(
                {
                    ...headers,
                    ...(useDevEsi && {[DEV_ESI_HEADER]: plpHeaders[DEV_ESI_HEADER]}),
                },
                itemsString,
                apiProxyVersion,
                siteUrl,
            )
            const products = mapProductsFragmentJsonToItems(fragment || "")
            return updateProductSummaryItemsData(products.items as MapProductsFragmentJsonToItemsResponseItems[], {
                dispatch,
                getState,
            })
        }

        const productSummaryData = items.map(async item => {
            const {response} = await getProductSummaryJson(
                headers,
                apiProxyVersion,
                item?.itemNumber,
                item?.type || ProductType,
            )
            if (!response) return {itemNumber: item?.itemNumber, isConfError: true}

            response.type = response.type.toLowerCase() as SummaryType
            response.newIn = !!item?.newIn

            let {colourways} = response
            let colourwaysHasSale = false
            let colourwaysHasStarRatingWithRangedPrice = false
            let colourwaysHasRating = false

            // eslint-disable-next-line no-restricted-syntax
            for (const colourway of colourways) {
                colourwaysHasSale = colourwaysHasSale || Boolean(colourway.sp)
                colourwaysHasStarRatingWithRangedPrice =
                    colourwaysHasStarRatingWithRangedPrice ||
                    (Boolean(colourway.st) && Boolean(colourway.p?.includes("-")))
                colourwaysHasRating = colourwaysHasRating || (colourway?.st != null ? colourway.st > 0 : false)

                if (colourwaysHasSale && colourwaysHasStarRatingWithRangedPrice && colourwaysHasRating) {
                    break
                }
            }

            if (maxNumberOfSofaColourways && response.type === "sofa") {
                colourways = response.colourways.slice(0, maxNumberOfSofaColourways)
            }
            if (maxNumberOfHomewareColourways && response.department === "Homeware") {
                colourways = response.colourways.slice(0, maxNumberOfHomewareColourways)
            }
            colourways = colourways.slice(0, SHOW_INITIAL_COLOURCHIPS)

            return {
                ...response,
                colourways: colourways as any[],
                numberOfAllColourways: response.colourways.length,
                colourwaysHasSale,
                colourwaysHasStarRatingWithRangedPrice,
                colourwaysHasRating,
                isLoadingFav: false,
                animateFavouriteIcon: false,
                colourchipSelected: 0,
                showFavErrorToolTip: null,
                isFav: false,
                additionalColourchipsLoading: AdditionalColourchipsLoadingState.Default,
                itemNumber: item?.itemNumber,
                bundlePriceLabel: title,
            }
        })

        const resolvedProductSummaryData = await Promise.all(productSummaryData)
        const productSummaryDataObject = resolvedProductSummaryData.reduce((acc, item) => {
            acc[item.itemNumber] = item
            return acc
        }, {})

        dispatch<Action>(setProductSummaryItemsDataAction(productSummaryDataObject))
    } catch (err) {
        const error = err as AxiosError
        if (error.response) {
            dispatch<Action>(setProductSummaryItemsDataAction(initialState.summaryItemsData))
            return {errorStatusCode: error.response.status}
        }
        logger.error(error)
        dispatch<Action>(setProductSummaryItemsDataAction(initialState.summaryItemsData))
    }
}

export const fetchAdditionalColourchipsThunk =
    (itemNumber: string) => async (dispatch: Dispatch, getState: GetStateFn) => {
        dispatch<Action>(
            setAdditionalColourchipsLoading({
                itemNumber,
                additionalColourchipsLoading: AdditionalColourchipsLoadingState.Loading,
            }),
        )
        const state = getState()

        const summaryData = getTileSummaryData(state.productSummary.summaryItemsData, itemNumber)
        if (summaryData?.colourways?.length !== summaryData.numberOfAllColourways) {
            try {
                const {
                    search: {apiProxyVersion},
                    request: {headers},
                } = state
                const {type} = summaryData
                const productSummaryData = await getColourways(headers, apiProxyVersion, itemNumber, type)
                dispatch<Action>(
                    setAdditionalColourways({
                        itemNumber,
                        colourwayData: productSummaryData?.response?.colourways || [],
                    }),
                )
            } catch (error) {
                logger.error(error, "fetch-additional-colourchips-thunk")
            }
        }
        dispatch<Action>(
            setAdditionalColourchipsLoading({
                itemNumber,
                additionalColourchipsLoading: AdditionalColourchipsLoadingState.Loaded,
            }),
        )
    }

export const updateFavourites =
    (
        getFavouritesData: GetFavouritesCallbackContractModel,
        favType: typeof FAV_ADD | typeof FAV_REMOVE,
        itemNumber: string,
    ) =>
    (dispatch: Dispatch, getState: GetStateFn) => {
        const state = getState()
        if (getFavouritesData.success) {
            dispatch(
                setFavouritedColourways({
                    itemNumber,
                    favouritedColourways: shoppingListItemsToFavouritedColourways(
                        getFavouritesData?.data?.ShoppingListItems,
                    ),
                }),
            )
            dispatch<Action>(setLoadingFav({itemNumber, isLoadingFav: false, animateFavouriteIcon: false}))
            const eventActionType = favType === FAV_ADD ? GTM_ADD_FAVOURITES : GTM_REMOVE_FAVOURITES
            publishFavTrackEvent(state, eventActionType, itemNumber)
        } else {
            const errorType =
                getFavouritesData?.errorMessage === FAV_ERROR_LIMIT_EXCEEDED
                    ? FavErrorToolTipType.MaxLimit
                    : FavErrorToolTipType.Error
            dispatch(setShowFavErrorToolTip({itemNumber, errorType}))
            const eventActionType = favType === FAV_ADD ? GTM_ERROR_ADD_FAVOURITES : GTM_ERROR_REMOVE_FAVOURITES

            publishFavTrackEvent(state, eventActionType, itemNumber)

            logger.error("Product summary: favourites api failed")
            dispatch<Action>(setLoadingFav({itemNumber, isLoadingFav: false, animateFavouriteIcon: false}))
        }
    }

export const publishFavTrackEvent = (state: State, eventActionType: string, itemNumber: string) => {
    PublishTrackEvent(GTM_FAVOURITES, {
        event: GTM_FAVOURITES,
        favourites: {
            category: GTM_FAVOURITES,
            action: eventActionType,
            label: getProductTitle(state, itemNumber),
            option: itemNumber,
        },
    })
}

export const updateEnablingReviewStars = (dispatch: Dispatch, enableReviewStars: boolean): any => {
    dispatch(setEnableReviewStars(enableReviewStars))
}
export const updateEnablingBrandDisplay = (dispatch: Dispatch, enableBrandDisplay: boolean): any => {
    dispatch(setEnableBrandDisplay(enableBrandDisplay))
}

export const updateMobileThumbnailConfig = (dispatch: Dispatch, mobileThumbnailConfig): any => {
    dispatch(setMobileThumbnailConfig(mobileThumbnailConfig))
}

export const updateColourChipConfig = (dispatch: Dispatch, colourChipConfig: ColourChipConfig): any => {
    dispatch(setColourChipConfig(colourChipConfig))
}

export const updateMaxNumberOfSofaColourways = (dispatch: Dispatch, maxNumberOfSofaColourways: number | null): any => {
    dispatch(setMaxNumberOfSofaColourways(maxNumberOfSofaColourways))
}

export const updateMaxNumberOfHomewareColourways = (
    dispatch: Dispatch,
    maxNumberOfHomewareColourways: number | null,
): any => {
    dispatch(setMaxNumberOfHomewareColourways(maxNumberOfHomewareColourways))
}

export const getProductId = (state: State, itemNumber: string) => {
    const summaryData = getTileSummaryData(state.productSummary.summaryItemsData, itemNumber)
    return summaryData.id
}

export const updateImageRatio = (dispatch: Dispatch, imageRatio: string) => {
    return dispatch(SetImageRatio(imageRatio))
}

export const updateBrandedMarkdownSalePrice = (dispatch: Dispatch, brandedMarkdownSalePrice: boolean) => {
    return dispatch(SetBrandedMarkdownSalePrice(brandedMarkdownSalePrice))
}

export const updateDisplayFitIcons = (dispatch: Dispatch, displayFitIcons: boolean): any => {
    dispatch(SetDisplayFitIcons(displayFitIcons))
}

export const updateDisplayNewIn = (dispatch: Dispatch, displayNewIn: boolean): any => {
    dispatch(SetDisplayNewIn(displayNewIn))
}
export const updateEnableSearchDescription = (dispatch: Dispatch, enabledSearchDesc: boolean): any => {
    dispatch(setEnabledSearchDescription(enabledSearchDesc))
}

export const updateImageCDNUrl = (dispatch: Dispatch, imageCdnUrl: ImageCdnUrlParts): void => {
    dispatch(SetImageCdnUrl(imageCdnUrl))
}

export const updateSaleSashAndPosition = (
    dispatch: Dispatch,
    saleSashData: {saleSash: string; saleSashPosition: string},
): void => {
    dispatch(SetSaleSashAndPosition(saleSashData))
}

export const updateProductSummaryItemsData = (
    items: MapProductsFragmentJsonToItemsResponseItems[],
    {dispatch, getState}: {dispatch: Dispatch; getState: GetStateFn},
) => {
    try {
        const {
            search: {title},
            productSummary: {maxNumberOfSofaColourways, maxNumberOfHomewareColourways, summaryItemsData},
        } = getState() as State

        const productSummaryData = items.reduce((acc, item) => {
            if (summaryItemsData[item.itemNumber]) return acc
            const {itemNumber, newIn, type, ...rest} = item
            const response = item
            if (isEmpty(rest)) {
                acc[itemNumber] = {
                    ...response,
                    isConfError: true,
                }
                return acc
            }

            response.type = type.toLowerCase() as SummaryType
            response.newIn = !!newIn

            let {colourways} = response
            let colourwaysHasSale = false
            let colourwaysHasStarRatingWithRangedPrice = false
            let colourwaysHasRating = false

            // eslint-disable-next-line no-restricted-syntax
            for (const colourway of colourways) {
                colourwaysHasSale = colourwaysHasSale || Boolean(colourway.sp)
                colourwaysHasStarRatingWithRangedPrice =
                    colourwaysHasStarRatingWithRangedPrice ||
                    (Boolean(colourway.st) && Boolean(colourway.p?.includes("-")))
                colourwaysHasRating = colourwaysHasRating || (colourway?.st != null ? colourway.st > 0 : false)

                if (colourwaysHasSale && colourwaysHasStarRatingWithRangedPrice && colourwaysHasRating) {
                    break
                }
            }

            if (maxNumberOfSofaColourways && response.type === "sofa") {
                colourways = response.colourways.slice(0, maxNumberOfSofaColourways)
            }
            if (maxNumberOfHomewareColourways && response.department === "Homeware") {
                colourways = response.colourways.slice(0, maxNumberOfHomewareColourways)
            }
            colourways = colourways.slice(0, SHOW_INITIAL_COLOURCHIPS)

            acc[response.itemNumber] = {
                ...response,
                colourways: colourways as any[],
                numberOfAllColourways: response.colourways.length,
                colourwaysHasSale,
                colourwaysHasRating,
                colourwaysHasStarRatingWithRangedPrice,
                isLoadingFav: false,
                animateFavouriteIcon: false,
                colourchipSelected: 0,
                showFavErrorToolTip: null,
                isFav: false,
                additionalColourchipsLoading: AdditionalColourchipsLoadingState.Default,
                bundlePriceLabel: title,
            }
            return acc
        }, {})

        dispatch<Action>(setUpdateProductSummaryItemsData(productSummaryData))
    } catch (err) {
        const error = err as AxiosError
        logger.error(error)
    }
}

interface PopulateProductSummaryState {
    type: typeof POPULATE_PRODUCT_SUMMARY_STATE
    data: Partial<ProductSummaryState>
}

export const populateProductSummaryState = (dispatch: Dispatch, data: Partial<ProductSummaryState>): void => {
    dispatch({
        type: POPULATE_PRODUCT_SUMMARY_STATE,
        data,
    })
}

const reducer = (
    state: ProductSummaryState = initialState,
    action:
        | SetSelectedColourway
        | SetEnableReviewStars
        | SetShowFavErrorToolTip
        | SetEnableSearchDescription
        | SetLoadingFav
        | SetFavouritedColourways
        | SetAnimateFavouriteIcon
        | SetEnableBrandDisplay
        | SetEnableSingleLineTitle
        | SetSelectedColourchip
        | SetMobileThumbnailConfig
        | SetColourChipConfig
        | SetMaxNumberOfSofaColourways
        | SetMaxNumberOfHomewareColourways
        | SetAdditionalColourchipsLoading
        | SetAdditionalColourways
        | SetImageRatio
        | SetBrandedMarkdownSalePrice
        | SetAkamaiImageParams
        | SetDisplayFitIcons
        | SetDisplayNewIn
        | SetSummaryItemsData
        | SetImageCdnUrl
        | SetSaleSashAndPosition
        | UpdateSummaryItemsData
        | SetSelectedColourwayAndColourchip
        | PopulateProductSummaryState,
): ProductSummaryState => {
    switch (action.type) {
        case SET_SUMMARY_ITEMS_DATA: {
            return {
                ...state,
                summaryItemsData: action.data,
            }
        }
        case SET_SELECTED_COLOURWAY_AND_COLOURCHIP: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            const updatedColourways = (selectedItem.colourways as any[]).map(colourway => ({
                ...colourway,
                s: action.data.colourwayItemNumber === colourway.id,
            }))

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        colourchipSelected: action.data.colourchipItemNumber,
                        colourways: updatedColourways,
                        canLoadVisibleColourways: !selectedItem.hasLoadedVisibleColourways,
                        hasLoadedVisibleColourways: true,
                        isFav: !!updatedColourways.find(
                            colourway => action.data.colourwayItemNumber === colourway.id && colourway.isFav,
                        ),
                        animateFavouriteIcon: action.data.animateFavouriteIcon,
                    },
                },
            }
        }
        case SET_SELECTED_COLOURWAY: {
            const {itemNumber, colourwayItemNumber} = action.data
            const selectedItem = getTileSummaryData(state.summaryItemsData, itemNumber)

            const updatedColourways = (selectedItem.colourways as any[]).map(colourway => ({
                ...colourway,
                s: colourwayItemNumber === colourway.id,
            }))

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [itemNumber]: {
                        ...selectedItem,
                        colourways: updatedColourways,
                        canLoadVisibleColourways: !selectedItem.hasLoadedVisibleColourways,
                        hasLoadedVisibleColourways: true,
                        isFav: !!updatedColourways.find(
                            colourway => colourwayItemNumber === colourway.id && colourway.isFav,
                        ),
                    },
                },
            }
        }
        case SET_FAVOURITED_COLOURWAYS: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        colourways: (selectedItem.colourways as any[]).map(colourway => ({
                            ...colourway,
                            isFav: action.data.favouritedColourways.includes(colourway.id),
                        })),
                        isFav: !!(selectedItem.colourways as any[]).filter(
                            colourway => action.data.favouritedColourways.includes(colourway.id) && colourway.s,
                        ).length,
                    },
                },
            }
        }
        case SET_ADDITIONAL_COLOURWAYS: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        colourways: [
                            ...selectedItem.colourways,
                            ...action.data.colourwayData.slice(selectedItem.colourways.length),
                        ] as any[],
                    },
                },
            }
        }
        case SET_ENABLE_REVIEW_STARS:
            return {...state, enableReviewStars: action.enableReviewStars}
        case SET_ENABLE_BRAND_DISPLAY:
            return {...state, enableBrandDisplay: action.enableBrandDisplay}
        case SET_ENABLE_SINGLE_LINE_TITLE:
            return {...state, enableSingleLineTitle: action.enableSingleLineTitle}
        case SET_LOADING_FAV: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        isLoadingFav: action.data.isLoadingFav,
                        animateFavouriteIcon: action.data.animateFavouriteIcon,
                    },
                },
            }
        }
        case SET_ANIMATE_FAVOURITE_ICON: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        animateFavouriteIcon: action.data.animateFavouriteIcon,
                    },
                },
            }
        }
        case SHOW_FAV_ERROR_TOOLTIP: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        showFavErrorToolTip: action.data.errorType,
                    },
                },
            }
        }
        case SET_ENABLE_SEARCH_DESCRIPTION:
            return {...state, enabledSearchDesc: action.enabledSearchDesc}
        case SET_SELECTED_COLOURCHIP: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        colourchipSelected: action.data.colourchipItemNumber,
                    },
                },
            }
        }
        case SET_MOBILE_THUMBNAIL_CONFIG:
            return {...state, mobileThumbnailConfig: action.mobileThumbnailConfig}
        case SET_COLOUR_CHIP_CONFIG:
            return {...state, colourChipConfig: action.colourChipConfig}
        case SET_MAX_NUMBER_OF_SOFA_COLOURWAYS:
            return {...state, maxNumberOfSofaColourways: action.maxNumberOfSofaColourways}
        case SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS:
            return {...state, maxNumberOfHomewareColourways: action.maxNumberOfHomewareColourways}
        case SET_ADDITIONAL_COLOURHIPS_LOADING: {
            const selectedItem = getTileSummaryData(state.summaryItemsData, action.data.itemNumber)

            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    [action.data.itemNumber]: {
                        ...selectedItem,
                        additionalColourchipsLoading: action.data.additionalColourchipsLoading,
                    },
                },
            }
        }
        case SET_IMAGE_RATIO:
            return {...state, imageRatio: action.imageRatio}
        case SET_BRANDED_MARKEDOWN_SALE_PRICE:
            return {...state, brandedMarkdownSalePrice: action.brandedMarkdownSalePrice}
        case SET_AKAMAI_IMAGE_PARAMS:
            return {...state, akamaiImageParams: action.data}
        case SET_DISPLAY_FIT_ICONS:
            return {...state, displayFitIcons: action.displayFitIcons}
        case SET_DISPLAY_NEW_IN:
            return {...state, displayNewIn: action.displayNewIn}
        case SET_IMAGE_CDN_URL:
            return {...state, ...action.imageCdnUrl}
        case SET_SALE_SASH_AND_POSITION:
            return {
                ...state,
                ...action.data,
            }
        case UPDATE_SUMMARY_ITEMS_DATA:
            return {
                ...state,
                summaryItemsData: {
                    ...state.summaryItemsData,
                    ...action.data,
                },
            }
        case POPULATE_PRODUCT_SUMMARY_STATE:
            return {
                ...state,
                ...action.data,
            }
        default:
            return state
    }
}

export default reducer
